import { get } from 'lodash';
import { pruneFieldErrors } from '@/utils/errors';
import findResult, { hasKey } from '@/utils/resultsHelper';
import pagingWithFilteredResults, {
  pagingWithResults,
  presentationOptions,
} from '@/constants/states';
import fields from '@/constants/fields';
import { readStates, saveStates, states } from '@/constants/lifecycle';
import { roleStates, roleGroupings } from '../constants';
import { roleActionsDialogState } from '../roleactions/state/state';

const roleTypeFilters = (userFlag = true, tokenFlag = true) => ({
  userFlag,
  tokenFlag,
});

export const activeRole = () => ({
  open: false,
  confirmChangesOpen: false,
  code: null,
  name: '',
  description: '',
  grouping: null,
  role: 'standard',
  tokenRole: 'read-metrics-token',
  max_active_tokens: 0,
  max_token_length_days: 0,
  ...roleStates,
});

export const defaultRole = (readStatus = states.NONE) => ({
  code: null,
  readStatus,
  readErrors: [],
});

export const rolePanels = (openPanels = [0]) => ({
  panels: {
    single: openPanels,
    left: openPanels,
    right: openPanels,
  },
});

const allRoles = (userFlag, tokenFlag, display = presentationOptions.TABLE) => (
  {
    all: {
      ...pagingWithFilteredResults(),
      ...roleTypeFilters(userFlag, tokenFlag),
      display,
      ...roleStates,
    },
  }
);

export const rolesReassign = () => (
  {
    ...pagingWithResults(10),
    ...roleTypeFilters(true, false),
    ...roleStates,
    users: [],
    open: false,
    assignable: true,
    multiSelect: false,
  }
);

export const rolesToSetAsDefault = () => (
  {
    ...pagingWithResults(10),
    ...roleTypeFilters(true, false),
    ...readStates,
    ...saveStates,
    open: false,
    multiSelect: false,
    assignable: true,
  }
);

const initialState = {
  activeRole: activeRole(),
  activeRoleActions: roleActionsDialogState(),
  roles: {
    ...allRoles(true, true),
    lookups: {
      ...pagingWithResults(10000),
      ...roleTypeFilters(true, false),
      ...roleStates,
      assignable: true,
    },
    users: {
      ...pagingWithResults(10000),
      ...roleTypeFilters(true, false),
      ...roleStates,
    },
    reassign: rolesReassign(),
    tokens: {
      ...pagingWithResults(10000),
      ...roleStates,
    },
    open: {
      results: [],
    },
    defaultRole: {
      results: [defaultRole()],
    },
    defaultSelection: rolesToSetAsDefault(),
  },
};

export const pruneRoleErrors = (errors, { properties }) => {
  let filteredErrors = errors;

  if (hasKey(properties, 'name')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.NAME);
  }
  if (hasKey(properties, 'code')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.CODE);
  }
  if (hasKey(properties, 'description')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.DESCRIPTION);
  }

  return filteredErrors;
};

export const newRoleProperties = (item, action) => ({
  category: action.category,
  open: true,
  role: (item?.grouping === roleGroupings.USERS) ? item.code : 'standard',
  tokenRole: (item?.grouping === roleGroupings.TOKENS) ? item.code : 'read-metrics-token',
  grouping: item?.grouping || roleGroupings.USERS,
  ...roleStates,
});

export const confirmPermissionsProperties = (item, action) => ({
  ...item,
  category: action.category,
  confirmChangesOpen: true,
  ...roleStates,
});

export const roleStateToLoad = ({ roles }) => {
  const items = get(roles, 'roles', initialState.roles);
  const allFilters = items.all || initialState.roles.all;
  return {
    ...initialState,
    roles: {
      ...initialState.roles,
      ...allRoles(allFilters.userFlag, allFilters.tokenFlag, allFilters.display),
    },
  };
};

export const roleStateToSave = ({ roles }) => (
  {
    roles: {
      roles: {
        all: {
          userFlag: roles.roles.all.userFlag,
          tokenFlag: roles.roles.all.tokenFlag,
          display: roles.roles.all.display,
        },
      },
    },
  }
);

export const findRoles = ({ roles }, { category }) => roles[category];

export const findRole = ({ roles }, { category, code }) => findResult(roles, category, code);

export default initialState;
